import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import EntityMember from "_shared/entity-members/entity-member";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import { UserAvatarMap } from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import * as React from "react";
import { useSession } from "users/__session/session-context";

const SubscriberMenu = styled(Popover)(() => ({
  zIndex: 99999,
}));
const SubscriberNotificationList = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(40),
  overflowY: "auto",
  maxWidth: theme.spacing(50),
  minWidth: theme.spacing(30),
}));
const SubscriberButtonSlot = styled("span")(() => ({
  alignContent: "center",
  alignSelf: "stretch",
  borderBottom: "1px solid #aaa",
  display: "flex",
  marginLeft: "auto",
}));
const SubscriberIcon = styled(IconButton)(() => ({
  alignSelf: "center",
  color: "#666",
  height: "fit-content",
  display: "flex",
}));
const SubscriberMenuTitle = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 0.5),
}));

type ChatComponentProps = {
  subscribers: Individual[];
  forum: Forum | undefined;
  avatars: UserAvatarMap;
  disableLeaveButton?: boolean;
  onAddSubscriber: () => void;
  onLeaveConversation: () => void;
};

export default function SubscribersComponent(props: Readonly<ChatComponentProps>) {
  const { subscribers, forum, avatars, disableLeaveButton, onAddSubscriber, onLeaveConversation } = props;

  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const { openDialog } = useDialog();
  const session = useSession();

  function openSubscriberProfile(subscriber: Individual) {
    setMenuAnchor(null);
    openDialog({
      component: <ViewIndividualProfile profile={subscriber.profile} individualId={subscriber.id} />,
      contentSxProps: {
        display: "flex",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
      titleStyle: {
        paddingBottom: 0,
      },
    });
  }

  async function leaveConversation() {
    if (!forum) return;
    const messageService = new MessagingAPIService(session);
    if (subscribers.length === 1) {
      await messageService.deleteForum(forum);
    } else {
      const newSubscribers = subscribers.filter((subscriber) => subscriber.id !== session?.user?.id);
      const updatedForum = Forum.create({
        ...forum,
        subscribers: newSubscribers.map((individual) =>
          EntityMember.create({
            userId: individual.userId,
            legalEntityId: individual.legalEntityId,
          })
        ),
      });
      await messageService.updateSubscribersForForum(updatedForum);
    }
    onLeaveConversation();
  }

  return (
    <React.Fragment>
      <Tooltip title="Member List">
        <SubscriberButtonSlot>
          <SubscriberIcon
            aria-label="open subscriber list"
            disabled={!forum}
            onClick={(event) => setMenuAnchor(menuAnchor ? null : event.currentTarget)}
            size="large"
          >
            <Badge badgeContent={subscribers?.length}>
              <GroupIcon />
            </Badge>
          </SubscriberIcon>
        </SubscriberButtonSlot>
      </Tooltip>
      <SubscriberMenu
        anchorEl={menuAnchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <SubscriberMenuTitle variant="subtitle2">Chat Members</SubscriberMenuTitle>
        <SubscriberNotificationList dense>
          {subscribers.map((subscriber: Individual) => (
            <ListItemButton key={subscriber.id.value} onClick={() => openSubscriberProfile(subscriber)}>
              <ListItemAvatar>
                <Avatar src={avatars[subscriber.id.value]} />
              </ListItemAvatar>
              <ListItemText primary={subscriber.fullName} secondary={subscriber.company?.profile?.name} />
            </ListItemButton>
          ))}
        </SubscriberNotificationList>
        <Divider />
        {forum?.creator && (
          <List dense>
            <ListItemButton
              onClick={() => {
                setMenuAnchor(null);
                onAddSubscriber();
              }}
            >
              <ListItemIcon>
                <GroupAddIcon />
              </ListItemIcon>
              <ListItemText>Add People</ListItemText>
            </ListItemButton>
            {!disableLeaveButton && (
              <ListItemButton
                onClick={() => {
                  setMenuAnchor(null);
                  void leaveConversation();
                }}
              >
                <ListItemIcon>
                  <ExitToAppIcon />
                </ListItemIcon>
                <ListItemText>Leave Conversation</ListItemText>
              </ListItemButton>
            )}
          </List>
        )}
      </SubscriberMenu>
    </React.Fragment>
  );
}
